/**
 * ConnectPath Spacing System
 * 
 * This file defines the spacing scale for the ConnectPath design system.
 * It provides a consistent set of spacing values for margins, paddings, and layout.
 */

// Base spacing unit (in pixels)
const BASE_UNIT = 4;

// Spacing scale
export const spacing = {
  // Named spacing values
  none: '0',
  xs: `${BASE_UNIT}px`,           // 4px
  sm: `${BASE_UNIT * 2}px`,       // 8px
  md: `${BASE_UNIT * 4}px`,       // 16px
  lg: `${BASE_UNIT * 6}px`,       // 24px
  xl: `${BASE_UNIT * 8}px`,       // 32px
  '2xl': `${BASE_UNIT * 12}px`,   // 48px
  '3xl': `${BASE_UNIT * 16}px`,   // 64px
  '4xl': `${BASE_UNIT * 24}px`,   // 96px
  '5xl': `${BASE_UNIT * 32}px`,   // 128px
  
  // Numeric spacing values (for direct access)
  0: '0',
  1: `${BASE_UNIT / 4}px`,        // 1px
  2: `${BASE_UNIT / 2}px`,        // 2px
  4: `${BASE_UNIT}px`,            // 4px
  8: `${BASE_UNIT * 2}px`,        // 8px
  12: `${BASE_UNIT * 3}px`,       // 12px
  16: `${BASE_UNIT * 4}px`,       // 16px
  20: `${BASE_UNIT * 5}px`,       // 20px
  24: `${BASE_UNIT * 6}px`,       // 24px
  32: `${BASE_UNIT * 8}px`,       // 32px
  40: `${BASE_UNIT * 10}px`,      // 40px
  48: `${BASE_UNIT * 12}px`,      // 48px
  56: `${BASE_UNIT * 14}px`,      // 56px
  64: `${BASE_UNIT * 16}px`,      // 64px
  80: `${BASE_UNIT * 20}px`,      // 80px
  96: `${BASE_UNIT * 24}px`,      // 96px
  128: `${BASE_UNIT * 32}px`,     // 128px
};

// Layout-specific spacing
export const layout = {
  // Container widths
  container: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  
  // Section spacing
  section: {
    sm: spacing.lg,
    md: spacing.xl,
    lg: spacing['2xl'],
    xl: spacing['3xl'],
  },
  
  // Content spacing
  content: {
    sm: spacing.md,
    md: spacing.lg,
    lg: spacing.xl,
  },
  
  // Grid gaps
  grid: {
    sm: spacing.sm,
    md: spacing.md,
    lg: spacing.lg,
  },
};

// Inset (padding) shortcuts
export const inset = {
  // Symmetric insets (same padding on all sides)
  xs: {
    padding: spacing.xs,
  },
  sm: {
    padding: spacing.sm,
  },
  md: {
    padding: spacing.md,
  },
  lg: {
    padding: spacing.lg,
  },
  xl: {
    padding: spacing.xl,
  },
  
  // Squish insets (more horizontal padding than vertical)
  squish: {
    xs: {
      paddingTop: spacing.xs,
      paddingBottom: spacing.xs,
      paddingLeft: spacing.sm,
      paddingRight: spacing.sm,
    },
    sm: {
      paddingTop: spacing.sm,
      paddingBottom: spacing.sm,
      paddingLeft: spacing.md,
      paddingRight: spacing.md,
    },
    md: {
      paddingTop: spacing.md,
      paddingBottom: spacing.md,
      paddingLeft: spacing.lg,
      paddingRight: spacing.lg,
    },
  },
  
  // Stretch insets (more vertical padding than horizontal)
  stretch: {
    xs: {
      paddingTop: spacing.sm,
      paddingBottom: spacing.sm,
      paddingLeft: spacing.xs,
      paddingRight: spacing.xs,
    },
    sm: {
      paddingTop: spacing.md,
      paddingBottom: spacing.md,
      paddingLeft: spacing.sm,
      paddingRight: spacing.sm,
    },
    md: {
      paddingTop: spacing.lg,
      paddingBottom: spacing.lg,
      paddingLeft: spacing.md,
      paddingRight: spacing.md,
    },
  },
};

// Export all spacing
const spacingSystem = {
  spacing,
  layout,
  inset,
};

export default spacingSystem;